/**
 * imageUtils.ts — Shared image processing utilities.
 *
 * Used by the Lexical editor (DragDropPastePlugin) to compress images before
 * storing them in Yjs docs.
 *
 * S3 swap point: replace `compressImage` with an upload call that returns a
 * CDN URL. Callers receive the same shape `{ dataURL, mimeType, width, height }`.
 */

// Max pixel dimension for stored images — keeps Yjs doc size manageable
export const MAX_STORAGE_PX = 1920;
// WebP quality for compressed output (0–1)
export const WEBP_QUALITY = 0.85;

// Types that cannot be meaningfully compressed via canvas — pass through as-is
export const PASSTHROUGH_MIME_TYPES = new Set(['image/svg+xml', 'image/gif']);

export interface CompressedImage {
  dataURL: string;
  mimeType: string;
  width: number;
  height: number;
}

/** Read a File as a base64 data URL (raw, no compression). */
export function fileToDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Load an image element from a data URL.
 * Returns natural dimensions once loaded.
 */
function loadImage(dataURL: string): Promise<{ img: HTMLImageElement; width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ img, width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve({ img, width: 400, height: 300 });
    img.src = dataURL;
  });
}

/**
 * Compress an image file to a WebP data URL via an offscreen canvas.
 * - Caps resolution at MAX_STORAGE_PX on the longest edge
 * - SVG and GIF are passed through raw (canvas flattens animation/vectors)
 * - WebP gives ~25-35% smaller files than JPEG at the same quality setting
 * - S3 swap point: replace this function body with upload → return CDN URL
 */
export async function compressImage(file: File): Promise<CompressedImage> {
  const rawDataURL = await fileToDataURL(file);

  // Pass through types that don't compress well via canvas
  if (PASSTHROUGH_MIME_TYPES.has(file.type)) {
    const { width, height } = await loadImage(rawDataURL);
    return { dataURL: rawDataURL, mimeType: file.type, width, height };
  }

  const { img, width: naturalWidth, height: naturalHeight } = await loadImage(rawDataURL);

  const scale = Math.min(1, MAX_STORAGE_PX / Math.max(naturalWidth, naturalHeight));
  const w = Math.round(naturalWidth * scale);
  const h = Math.round(naturalHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    // Canvas unavailable — fall back to raw
    return { dataURL: rawDataURL, mimeType: file.type, width: naturalWidth, height: naturalHeight };
  }

  ctx.drawImage(img, 0, 0, w, h);
  const dataURL = canvas.toDataURL('image/webp', WEBP_QUALITY);
  return { dataURL, mimeType: 'image/webp', width: w, height: h };
}
